import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { loadThoughtsMetadata } from '../components/unspokenThoughts/LoadMetadata';
import ArtPlaceholder from '../assets/images/ArtPlaceholder.svg';
import SearchBar from '../components/common/SearchBar';
import LayOut from '../components/common/Layout';
import Heading from '../components/common/Heading';
import { Routes } from '../utils/Routes';

export default function Thoughts() {
  const [loadedImages, setLoadedImages] = useState<{ [id: string]: boolean }>(
    {},
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [thoughtArt, setthoughtArt] = useState<any[]>([]);

  // Preload images and initialize metadata
  useEffect(() => {
    const artData = loadThoughtsMetadata();
    const preloadThumbnails = async () => {
      await Promise.all(
        artData.map((art) => {
          return new Promise<void>((resolve) => {
            const img = new Image();
            img.src = art.thumbnail;
            img.onload = () => resolve();
            img.onerror = () => resolve();
          });
        }),
      );
      setthoughtArt(artData);
    };

    preloadThumbnails();
  }, []);

  const handleImageLoad = (id: string) => {
    setLoadedImages((prev) => ({
      ...prev,
      [id]: true,
    }));
  };

  const filteredThoughts = thoughtArt.filter((art) =>
    art.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const sortedThoughts = filteredThoughts.sort((a, b) =>
    a.title.localeCompare(b.title),
  );

  return (
    <LayOut layoutStyle="bg-white">
      <div className="pt-16 lg:pt-14">
        {/* Page Header */}
        <div className="text-center px-4 mb-8">
          <Heading text="Unspoken Thoughts" customStyle="text-custom-coffee" />
          <div className="mx-auto mt-3 w-10 h-px bg-custom-stone" />
        </div>

        <section className="flex flex-col items-center px-4 pb-10">
          {/* Search Bar */}
          <div className="w-full max-w-md mb-8">
            <SearchBar
              query={searchQuery}
              onSearch={setSearchQuery}
              placeholder="Search thoughts..."
            />
          </div>

          {/* Thoughts Grid */}
          <div className="container mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedThoughts.length > 0 ? (
              sortedThoughts.map((art) => (
                <Link
                  key={art.id}
                  to={`${Routes.Thoughts}/${art.id}`}
                  className="group block"
                >
                  <div className="overflow-hidden rounded-lg bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                    {/* Thumbnail */}
                    <div className="relative w-full h-48 overflow-hidden bg-gray-50">
                      {!loadedImages[art.id] && (
                        <img
                          src={ArtPlaceholder}
                          alt="Thoughts Placeholder"
                          className="w-full h-full object-cover"
                        />
                      )}
                      <img
                        src={art.thumbnail}
                        alt={`${art.title} Thought`}
                        className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${
                          loadedImages[art.id] ? 'block' : 'hidden'
                        }`}
                        onLoad={() => handleImageLoad(art.id)}
                        onError={(e) => {
                          e.currentTarget.src = ArtPlaceholder;
                        }}
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-custom-coffee/0 group-hover:bg-custom-coffee/10 transition-colors duration-300" />
                    </div>

                    {/* Content */}
                    <div className="px-4 py-4 border-t border-gray-100">
                      <h2 className="text-base font-semibold text-custom-coffee leading-snug">
                        {art.title || 'Untitled'}
                      </h2>
                      <p className="text-sm text-gray-500 mt-1.5 italic line-clamp-2">
                        {art.description || 'Explore the mood.'}
                      </p>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="text-center text-gray-400 col-span-full py-12 italic">
                No results found for &ldquo;{searchQuery}&rdquo;
              </div>
            )}
          </div>
        </section>
      </div>
    </LayOut>
  );
}
